import React from "react";
import "./ResponsiveTable.css";

/**
 * 子页面记录表的手机呈现：沿用同一套行、勾选、按钮和业务事件，不复制第二套可操作 DOM。
 * 简单表在手机上按记录排成带字段名的卡片；电脑保持原表格。排序/筛选表头、跨行矩阵、
 * 自定义行组件必须保留原来的表格关系，自动降级为有提示、可键盘滚动的横向表。
 * mobileLabels 可按实际列顺序提供字符串标签；className/style/ref 仍属于原 table，
 * wrapperClassName 属于外层滚动容器。mobile="scroll" 用于金额对账、工资、权限等比较矩阵。
 *
 * scrollOwner（2026-10-20 新增）：
 *   "self"（默认）—— 由本组件内部的 viewport 负责横向滚动，适合页面本身不滚的普通表格；
 *   "ancestor"   —— 由**调用方的外层容器**负责滚动，内部这层不再产生任何滚动容器。
 * 为什么需要它：`overflow-x: auto` 会让浏览器把 overflow-y 的计算值也变成 auto，
 * 内层 viewport 因此成为滚动容器，表头 sticky 的参照物就从页面自己的滚动盒变成了这层 ——
 * 设计项目矩阵（列头 top-7 吸顶）因此被整体下推 28px 压住第一行，表格视图的表头更是完全不吸顶。
 * 调用方已有 `overflow-auto` 卡片时传 "ancestor"，sticky 才能相对正确的那一层生效。
 */
function items(children) {
  return React.Children.toArray(children).flatMap(child => React.isValidElement(child) && child.type === React.Fragment ? items(child.props.children) : [child]);
}

function plainText(node) {
  return items(node).map(child => typeof child === "string" || typeof child === "number" ? String(child)
    : React.isValidElement(child) && typeof child.type === "string" ? plainText(child.props.children) : "").join(" ").replace(/\s+/g, " ").trim();
}

// 表头不隐藏任何可操作控件：包括行内点击排序和组件化的列筛选，不尝试猜测其业务含义。
function hasControl(node) {
  return items(node).some(child => React.isValidElement(child) && (
    (typeof child.type !== "string" && child.type !== React.Fragment) ||
    ["button", "input", "select", "textarea", "a"].includes(child.type) || child.props.onClick || child.props.onChange ||
    hasControl(child.props.children)
  ));
}

function mapFragments(children, fn) {
  return React.Children.map(children, child => React.isValidElement(child) && child.type === React.Fragment
    ? React.cloneElement(child, {}, mapFragments(child.props.children, fn)) : fn(child));
}

export const ResponsiveTable = React.forwardRef(function ResponsiveTable({ children, className = "", wrapperClassName = "", mobile = "cards", mobileLabels, scrollHint, lang, scrollOwner = "self", ...props }, ref) {
  const sections = items(children).filter(React.isValidElement);
  const heads = sections.filter(section => section.type === "thead");
  const headerRows = heads.flatMap(head => items(head.props.children).filter(React.isValidElement));
  const headerCells = headerRows.length === 1 ? items(headerRows[0].props.children).filter(React.isValidElement) : [];
  const labels = mobileLabels || headerCells.map(cell => plainText(cell.props.children));
  const bodyRows = sections.filter(section => ["tbody", "tfoot"].includes(section.type)).flatMap(section => items(section.props.children).filter(React.isValidElement));
  const complexRows = bodyRows.some(row => row.type !== "tr" || items(row.props.children).some(cell => React.isValidElement(cell) && (!["td", "th"].includes(cell.type) || Number(cell.props.rowSpan || 1) > 1)));
  const cards = mobile !== "scroll" && headerRows.length === 1 && labels.length > 0 && !complexRows &&
    !headerCells.some(cell => Number(cell.props.colSpan || 1) > 1 || Number(cell.props.rowSpan || 1) > 1) && !hasControl(heads);
  const zh = lang ? lang === "zh" : /[\u3400-\u9fff]/.test(labels.join("")) || (typeof document !== "undefined" && document.documentElement.lang.startsWith("zh"));
  // 卡片态本身不依赖内层滚动，只有滚动表格才需要关心「谁来滚」。
  const ancestorScrolls = !cards && scrollOwner === "ancestor";
  const decorated = cards ? mapFragments(children, section => {
    if (!React.isValidElement(section) || !["thead", "tbody", "tfoot"].includes(section.type)) return section;
    return React.cloneElement(section, { role: section.props.role || "rowgroup" }, mapFragments(section.props.children, row => {
      if (!React.isValidElement(row) || row.type !== "tr") return row;
      let column = 0;
      return React.cloneElement(row, { role: row.props.role || "row" }, mapFragments(row.props.children, cell => {
        if (!React.isValidElement(cell) || !["td", "th"].includes(cell.type)) return cell;
        const span = Number(cell.props.colSpan || 1);
        const label = span > 1 || section.type !== "tbody" ? "" : labels[column] || "";
        column += span;
        return React.cloneElement(cell, {
          role: cell.props.role || (section.type === "thead" ? "columnheader" : "cell"),
          "data-mobile-label": label,
          "data-mobile-wide": span > 1 || hasControl(cell.props.children) ? "true" : undefined,
        });
      }));
    }));
  }) : children;
  return <div className={`responsive-table-shell ${cards ? "responsive-table-cards" : "responsive-table-scroll"} ${wrapperClassName}`} data-mobile-table={cards ? "cards" : "scroll"}>
    {!cards && <p className="responsive-table-hint">{scrollHint || (zh ? "左右滑动查看完整表格，表头可继续排序和筛选" : "Swipe across for all columns; header controls remain available")}</p>}
    {/* ancestorScrolls 时这层只做语义容器（不再可聚焦 / 不再声明 region）：
        真正能滚的是调用方那个卡片，键盘焦点与 aria-label 也应由它承担（见 DesignV3Projects 的滚动盒）。 */}
    <div className={`responsive-table-viewport${ancestorScrolls ? " responsive-table-viewport-flat" : ""}`}
      tabIndex={cards || ancestorScrolls ? undefined : 0}
      role={cards || ancestorScrolls ? undefined : "region"}
      aria-label={cards || ancestorScrolls ? undefined : (props["aria-label"] || (zh ? "可横向滚动的数据表" : "Scrollable data table"))}>
      <table ref={ref} {...props} role={props.role || "table"} className={`responsive-data-table ${className}`}>{decorated}</table>
    </div>
  </div>;
});

export default ResponsiveTable;
